import type { ReactElement } from 'react';
import { useState } from 'react';
import MenuItem from 'models/MenuItem';
import { Component as CheckIcon } from 'icons/icon-ui--check.svg';
import { Component as CartIcon } from 'icons/icon-ui--cart-small.svg';
import Picture from 'components/shared/Picture';
import SaveOrDiscardModal from 'components/Cart/Header/SaveOrDiscardModal';
import ProductPackageSkuPrice from './ProductPackageSkuPrice';

type Props = {
  packageProduct: MenuItem;
  cartId: number;
  hasCartChanged: boolean;
  selected: boolean;
  toggleSelected: () => void;
  hasCartError: boolean;
  isPDPPackageProduct: boolean;
};

function ProductPackageSku({
  packageProduct,
  cartId,
  hasCartChanged,
  selected,
  toggleSelected,
  hasCartError,
  isPDPPackageProduct,
}: Props): ReactElement {
  const {
    title,
    brand,
    subtitle,
    servings,
    image_versions: imageVersions,
    link_to_product: linkToProduct,
    product_line_name: productLineName,
  } = packageProduct.sku;

  const addedToCart = packageProduct.quantity > 0;
  const linkWithCartId = `${linkToProduct}?cart_id=${cartId}`;

  const [
    showSaveOrDiscardModal,
    setShowSaveOrDiscardModal,
  ] = useState(false);

  const productType = (() => {
    switch (true) {
      case packageProduct.isBundle:
        return 'Grocery Bundle';
      case packageProduct.isSnack:
        return `${brand}®`;
      case packageProduct.isPreparedMeal:
        return 'Ready To Eat';
      default:
        return `${servings} Serving ${productLineName}`;
    }
  })();

  const handleRecipeClick = (e) => {
    if (hasCartChanged && hasCartError) {
      e.preventDefault();
      setShowSaveOrDiscardModal(true);
    }
  };

  const handleDiscard = () => {
    window.location.href = linkWithCartId;
  };

  return (
    <div>
      <Picture versions={imageVersions} useHighResOnly alt={title} />
      <div className="c-product-package-sku__content">
        <div className="c-product-package-sku__brand-name">
          {productType}
        </div>
        <a
          className="c-product-package-sku__title"
          href={linkWithCartId}
          onClick={(e) => handleRecipeClick(e)}
        >
          <div>
            <h3>
              {title}
              <br />
              <span className="c-product-package-sku__subtitle">
                {subtitle}
              </span>
            </h3>
          </div>
        </a>
        <div
          className="c-product-package-sku__price"
        >
          {!addedToCart ? (
            <ProductPackageSkuPrice
              sku={packageProduct.sku}
              isBundle={packageProduct.isBundle}
                // @ts-expect-error TODO: fix null safety
              price={null}
              isPDPPackageProduct={isPDPPackageProduct}
            />
          ) : (<br />)}
        </div>

        {!addedToCart ? (
          <>
            <div
              className="c-product-package-sku__added"
              onClick={toggleSelected}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  toggleSelected();
                }
              }}
              role="checkbox"
              aria-checked={selected}
              tabIndex={0}
            >
              <input
                className="c-product-package-sku__checkbox"
                type="checkbox"
                checked={selected}
                readOnly
              />
              <span
                className="c-product-package-sku__checkmark"
                style={{
                  backgroundColor: selected ? '#333' : 'transparent',
                }}
              >
                {selected && (
                  <CheckIcon className="c-svg-fill-white-color" width="80%" />
                )}
              </span>
            </div>
          </>
        ) : (
          <div className="c-product-package-sku__added">
            <CartIcon className="c-svg-stroke-primary-alt-color" />
            <span> In my cart</span>
          </div>
        )}
      </div>

      {showSaveOrDiscardModal && (
        <SaveOrDiscardModal
          onClose={() => setShowSaveOrDiscardModal(false)}
          onDiscard={handleDiscard}
          viewRecipeLink={linkWithCartId}
        />
      )}
    </div>
  );
}
export default ProductPackageSku;
